import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, ClipboardButton, Dialog, Icon, Label, Switch, Table, Text, TextInput, useToaster } from '@gravity-ui/uikit';
import { Link as LinkIcon, Plus } from '@gravity-ui/icons';
import type { ReferralLinkView } from '@odonix/contracts';
import { useTenantsApi } from '../../lib/api/context.js';
import { ApiError } from '../../lib/api/tenants.js';
import { count, day } from '../../ui/format.js';
import { Page, Section } from '../../ui/page.js';
import { Field } from '../../ui/field.js';
import { Empty, ErrorNote, LoadingRows } from '../../ui/states.js';

/**
 * Реферальные ссылки менеджера. Клиника, зарегистрировавшаяся по ссылке,
 * закрепляется за ним сама. Код можно задать свой — его диктуют по телефону.
 */
export function LinksScreen() {
  const api = useTenantsApi();
  const [creating, setCreating] = useState(false);
  const links = useQuery({ queryKey: ['my-links'], queryFn: () => api.links() });

  return (
    <Page
      title="Мои ссылки"
      description="Отправьте ссылку клинике. Кто зарегистрируется по ней — станет вашей клиникой, и с её подписки пойдёт ваш процент."
      actions={
        <Button
          view="action"
          onClick={() => {
            setCreating(true);
          }}
        >
          <Icon data={Plus} size={16} />
          Новая ссылка
        </Button>
      }
    >
      {links.isError ? (
        <ErrorNote title="Ссылки не загрузились" error={links.error} onRetry={() => void links.refetch()} />
      ) : links.data === undefined ? (
        <LoadingRows rows={4} />
      ) : links.data.length === 0 ? (
        <Empty
          title="Ссылок пока нет"
          description="Заведите первую: код подберётся из вашей фамилии, или задайте свой."
          action={
            <Button
              view="action"
              onClick={() => {
                setCreating(true);
              }}
            >
              Новая ссылка
            </Button>
          }
        />
      ) : (
        <Section flush>
          <div>
            <Table
              className="w-full"
              edgePadding
              stickyHorizontalScroll
              data={links.data}
              getRowDescriptor={(row) => ({ id: row.id })}
              columns={[
                {
                  id: 'url',
                  name: 'Ссылка',
                  template: (row) => (
                    <span className="flex items-center gap-1.5">
                      <Icon data={LinkIcon} size={14} className="text-[var(--g-color-text-secondary)]" />
                      <span className="break-all">{row.url}</span>
                      <ClipboardButton text={row.url} size="xs" />
                    </span>
                  ),
                },
                { id: 'label', name: 'Подпись', template: (row) => row.label ?? <Text color="secondary">—</Text> },
                { id: 'visits', name: 'Переходов', align: 'end', template: (row) => <span className="tabular">{count(row.visits)}</span> },
                { id: 'recent', name: 'За 30 дней', align: 'end', template: (row) => <span className="tabular">{count(row.visits30d)}</span> },
                { id: 'regs', name: 'Клиник', align: 'end', template: (row) => <span className="tabular">{count(row.registrations)}</span> },
                {
                  id: 'state',
                  name: '',
                  template: (row) => <LinkSwitch link={row} />,
                },
                { id: 'created', name: 'Создана', template: (row) => <span className="tabular">{day(row.createdAt)}</span> },
              ]}
            />
          </div>
        </Section>
      )}
      {creating ? (
        <CreateLinkDialog
          onClose={() => {
            setCreating(false);
          }}
        />
      ) : null}
    </Page>
  );
}

function LinkSwitch({ link }: { link: ReferralLinkView }) {
  const api = useTenantsApi();
  const queryClient = useQueryClient();
  const toaster = useToaster();
  const active = link.disabledAt === null;
  const mutation = useMutation({
    mutationFn: (next: boolean) => api.updateLink(link.id, { active: next }),
    onSuccess: async (updated) => {
      await queryClient.invalidateQueries({ queryKey: ['my-links'] });
      toaster.add({
        name: `link-${link.id}`,
        title: updated.disabledAt === null ? 'Ссылка включена' : 'Ссылка выключена',
        content: updated.disabledAt === null ? undefined : 'Новые регистрации по ней больше не закрепляются за вами.',
        theme: 'success',
        autoHiding: 4000,
      });
    },
  });

  return (
    <span className="flex items-center gap-2">
      <Switch
        checked={active}
        disabled={mutation.isPending}
        onUpdate={(next) => {
          mutation.mutate(next);
        }}
        content={active ? 'Работает' : 'Выключена'}
      />
      {active ? null : <Label theme="unknown" size="xs">выкл.</Label>}
    </span>
  );
}

function CreateLinkDialog({ onClose }: { onClose: () => void }) {
  const api = useTenantsApi();
  const queryClient = useQueryClient();
  const toaster = useToaster();
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      api.createLink({
        ...(code.trim() === '' ? {} : { code: code.trim() }),
        ...(label.trim() === '' ? {} : { label: label.trim() }),
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['my-links'] });
      toaster.add({ name: 'link-created', title: 'Ссылка создана', content: created.url, theme: 'success', autoHiding: 6000 });
      onClose();
    },
  });

  const error = mutation.error;

  return (
    <Dialog open onClose={onClose} size="s" aria-label="Новая ссылка">
      <Dialog.Header caption="Новая ссылка" />
      <Dialog.Body>
        <div className="flex flex-col gap-4">
          <Text color="secondary">
            Пустой код — подберём из фамилии. Свой код: латиница, цифры и дефис, от 3 знаков. Его потом не изменить.
          </Text>
          <Field label="Код" error={error instanceof ApiError ? error.message : undefined}>
            {(id) => <TextInput id={id} value={code} onUpdate={setCode} placeholder="ivanova" autoFocus />}
          </Field>
          <Field label="Подпись" hint="Для себя: откуда ссылка. Клиника её не видит.">
            {(id) => <TextInput id={id} value={label} onUpdate={setLabel} placeholder="Инстаграм" />}
          </Field>
        </div>
      </Dialog.Body>
      <Dialog.Footer
        textButtonApply="Создать"
        textButtonCancel="Отмена"
        onClickButtonApply={() => {
          mutation.mutate();
        }}
        onClickButtonCancel={onClose}
        loading={mutation.isPending}
      />
    </Dialog>
  );
}
